import { c as _c } from "react/compiler-runtime";
import { type MarketParams, MathLib } from '@morpho-org/blue-sdk';
import { useMemo } from 'react';
import { useIsProtectedLltvPending, useProtectedLltv } from '@/components/common/AutoDeleverage/ProtectedLltv';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getBorrowCeilingLtv, getDisplayedMaxLtv } from '../marketMaxLtv';
import { ACCRUAL_BUFFER, computeMaxWithdrawCollateralAssets } from './marketMaxAmounts';
import { useMarketBlue } from './useMarketBlue';
export interface UseMarketMaxWithdrawCollateralV2Params {
  /** Target market params (for the fresh on-chain position via useMarketBlue). */
  marketParams: MarketParams | undefined;
  /** Loan repaid atomically in the same combo (frees collateral); 0 for a plain withdraw. */
  proposedLoanRepay: bigint;
  /** The combo fully closes the debt (shares-mode repay) — then all collateral is withdrawable. */
  isMaxRepay: boolean;
  /** `marketRepayWithdraw` flow flag — gates the position fetch. */
  enabled: boolean;
}

/**
 * Max withdrawable collateral for the market-actions-v2 path (WS7), the blue-sdk
 * sister of the legacy `simulation-sdk`-sourced `useGetUserMaxMarketLoanWithdraw`.
 *
 * Sources the fresh on-chain position from {@link useMarketBlue} (shared with the
 * builder's query cache) and bounds the withdraw at the tighter of the
 * AutoDeleverage cap and the morpho-sdk safe-LTV (`getDisplayedMaxLtv`), so the
 * displayed max never reverts at build time (S2). Repaying in the same combo
 * frees more collateral.
 */
export function useMarketMaxWithdrawCollateralV2(t0) {
  const $ = _c(24);
  const {
    marketParams,
    proposedLoanRepay,
    isMaxRepay,
    enabled
  } = t0;
  const {
    account,
    targetChainId
  } = useWeb3Context();
  const {
    positionData,
    snapshotTimestamp,
    isLoading
  } = useMarketBlue(enabled ? marketParams : undefined);
  const t1 = marketParams?.lltv;
  const t2 = marketParams?.id;
  let t3;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== targetChainId) {
    t3 = {
      lltv: t1,
      marketId: t2,
      chainId: targetChainId
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = targetChainId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const protectedLltv = useProtectedLltv(t3);
  const t4 = marketParams?.id;
  let t5;
  if ($[4] !== t4 || $[5] !== targetChainId) {
    t5 = {
      marketId: t4,
      chainId: targetChainId
    };
    $[4] = t4;
    $[5] = targetChainId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const isProtectedLltvPending = useIsProtectedLltvPending(t5);
  let t6;
  bb0: {
    if (!enabled || !positionData || !protectedLltv || snapshotTimestamp == null) {
      let t7;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = {
          maxWithdraw: undefined,
          maxWithdrawCeiling: undefined
        };
        $[7] = t7;
      } else {
        t7 = $[7];
      }
      t6 = t7;
      break bb0;
    }
    const t7 = MathLib.max(snapshotTimestamp, positionData.market.lastUpdate) + ACCRUAL_BUFFER;
    let t8;
    let t9;
    if ($[8] !== isMaxRepay || $[9] !== positionData.borrowShares || $[10] !== positionData.collateral || $[11] !== positionData.market || $[12] !== proposedLoanRepay || $[13] !== protectedLltv || $[14] !== t7) {
      const base = {
        market: positionData.market,
        collateral: positionData.collateral,
        borrowShares: positionData.borrowShares,
        proposedLoanRepay,
        isMaxRepay,
        accrualTimestamp: t7
      };
      const {
        lltv
      } = positionData.market.params;
      t8 = computeMaxWithdrawCollateralAssets({
        ...base,
        maxLtv: getDisplayedMaxLtv({
          lltv,
          protectedLltv
        })
      });
      t9 = computeMaxWithdrawCollateralAssets({
        ...base,
        maxLtv: getBorrowCeilingLtv(protectedLltv)
      });
      $[8] = isMaxRepay;
      $[9] = positionData.borrowShares;
      $[10] = positionData.collateral;
      $[11] = positionData.market;
      $[12] = proposedLoanRepay;
      $[13] = protectedLltv;
      $[14] = t7;
      $[15] = t8;
      $[16] = t9;
    } else {
      t8 = $[15];
      t9 = $[16];
    }
    let t10;
    if ($[17] !== t8 || $[18] !== t9) {
      t10 = {
        maxWithdraw: t8,
        maxWithdrawCeiling: t9
      };
      $[17] = t8;
      $[18] = t9;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    t6 = t10;
  }
  const {
    maxWithdraw,
    maxWithdrawCeiling
  } = t6;
  const t7 = enabled && (isProtectedLltvPending || account.isConnected && isLoading);
  let t8;
  if ($[20] !== maxWithdraw || $[21] !== maxWithdrawCeiling || $[22] !== t7) {
    t8 = {
      maxWithdraw,
      maxWithdrawCeiling,
      isMaxLoading: t7
    };
    $[20] = maxWithdraw;
    $[21] = maxWithdrawCeiling;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}